import { useState, type ReactNode } from "react";

/**
 * A read-only value with a Copy button beside it.
 *
 * Selecting on focus AS WELL AS offering the button: `navigator.clipboard`
 * needs a secure context and a permission a browser can refuse, and the values
 * shown this way — a share link, a signing link, a minted credential — cannot
 * be shown a second time. The manual path has to work.
 *
 * Lived inside `SurveySharePanel` until the contract needed the same thing.
 * Two screens writing out the same fallback is how one of them ends up without
 * it.
 */
export function CopyField({
  label,
  value,
  hint,
  copies,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint: string;
  /** What the button copies, for a screen reader: a panel may show two. */
  readonly copies: string;
}): ReactNode {
  const [copied, setCopied] = useState<"idle" | "copied" | "failed">("idle");
  return (
    <div className="survey-share__copy">
      <label className="field">
        <span>{label}</span>
        <input
          type="text"
          readOnly
          value={value}
          aria-label={label}
          onFocus={(e) => e.currentTarget.select()}
        />
      </label>
      <div className="row-actions">
        <button
          type="button"
          className="primary"
          aria-label={`Copy the ${copies}`}
          onClick={() => {
            void navigator.clipboard
              .writeText(value)
              .then(() => setCopied("copied"))
              .catch(() => setCopied("failed"));
          }}
        >
          Copy
        </button>
        {copied === "copied" ? <span className="small">Copied.</span> : null}
        {copied === "failed" ? (
          <span className="small">Could not copy — select it and copy by hand.</span>
        ) : null}
      </div>
      <p className="small muted">{hint}</p>
    </div>
  );
}
